import { CANVAS_HEIGHT, CANVAS_WIDTH, type Slide, type SlideElement } from '~/design-system/components/DeckGen/types/presentation'

export const SLIDE_CANVAS_GAP = 120
export const SLIDE_ROW_OFFSET_Y = 120

const SLIDE_SLOT_WIDTH = CANVAS_WIDTH
export const SLIDE_SLOT_HEIGHT = CANVAS_HEIGHT

export function getSlideSlotPitchX() {
  return CANVAS_WIDTH + SLIDE_CANVAS_GAP
}

function getSlideSlotPitchY() {
  return CANVAS_HEIGHT + SLIDE_CANVAS_GAP
}

function getSlideGridColRow(slide: Pick<Slide, 'gridCol' | 'gridRow'>, fallbackIndex: number) {
  return {
    col: slide.gridCol ?? fallbackIndex,
    row: slide.gridRow ?? 0,
  }
}

export function getSlideGridWorldPosition(col: number, row: number) {
  return {
    x: col * getSlideSlotPitchX(),
    y: SLIDE_ROW_OFFSET_Y + row * getSlideSlotPitchY(),
  }
}

/**
 * World top-left of a slide. Free `x`/`y` is the source of truth when present; otherwise we fall back to
 * the legacy grid math (so decks that predate free positioning, and fresh AI output that only carries
 * `gridCol`/`gridRow`, still render where they used to). Once a slide is moved it gains `x`/`y` and the
 * grid is never consulted for it again.
 */
export function getSlideWorldPositionForSlide(
  slide: Pick<Slide, 'x' | 'y' | 'gridCol' | 'gridRow'>,
  fallbackIndex: number,
) {
  if (typeof slide.x === 'number' && typeof slide.y === 'number') {
    return { x: slide.x, y: slide.y }
  }
  const { col, row } = getSlideGridColRow(slide, fallbackIndex)
  return getSlideGridWorldPosition(col, row)
}

/** @deprecated Use getSlideWorldPositionForSlide when slide grid coords are available. */
function getSlideWorldPosition(slideIndex: number) {
  return getSlideGridWorldPosition(slideIndex, 0)
}

function getSlideSlotRect(col: number, row: number) {
  const { x, y } = getSlideGridWorldPosition(col, row)
  return {
    x,
    y,
    width: SLIDE_SLOT_WIDTH,
    height: SLIDE_SLOT_HEIGHT,
  }
}

/** Translate a set of elements by a world delta (used to convert between slide-local and world). */
export function translateElements(elements: SlideElement[], dx: number, dy: number): SlideElement[] {
  return elements.map((el) => ({ ...el, x: Math.round(el.x + dx), y: Math.round(el.y + dy) }))
}

/** The id of the slide whose world rect contains the point, or null (slides don't overlap). */
export function getSlideAtWorldPoint(
  slides: ReadonlyArray<{ id: string; worldX: number; worldY: number }>,
  worldX: number,
  worldY: number,
): string | null {
  for (const slide of slides) {
    if (
      worldX >= slide.worldX &&
      worldX <= slide.worldX + CANVAS_WIDTH &&
      worldY >= slide.worldY &&
      worldY <= slide.worldY + CANVAS_HEIGHT
    ) {
      return slide.id
    }
  }
  return null
}

/** True when a slide's world rect intersects `rect` (expanded by `margin`). Replaces the old grid-cell
 *  bounds test now that slides are freely positioned — viewport culling is a plain AABB overlap. */
export function isSlideWithinWorldBounds(
  slide: Pick<Slide, 'x' | 'y' | 'gridCol' | 'gridRow'>,
  fallbackIndex: number,
  rect: { minX: number; minY: number; maxX: number; maxY: number },
  margin: number,
) {
  const { x, y } = getSlideWorldPositionForSlide(slide, fallbackIndex)
  return (
    x + CANVAS_WIDTH >= rect.minX - margin &&
    x <= rect.maxX + margin &&
    y + CANVAS_HEIGHT >= rect.minY - margin &&
    y <= rect.maxY + margin
  )
}

/** @deprecated Use getSlideGridWorldPosition */
function getSlotWorldX(slotIndex: number) {
  return getSlideGridWorldPosition(slotIndex, 0).x
}

function getSlideWorldCenterForSlide(
  slide: Pick<Slide, 'x' | 'y' | 'gridCol' | 'gridRow'>,
  fallbackIndex: number,
) {
  const pos = getSlideWorldPositionForSlide(slide, fallbackIndex)
  return {
    x: pos.x + CANVAS_WIDTH / 2,
    y: pos.y + CANVAS_HEIGHT / 2,
  }
}

function getSlideWorldCenter(slideIndex: number) {
  const pos = getSlideWorldPosition(slideIndex)
  return {
    x: pos.x + CANVAS_WIDTH / 2,
    y: pos.y + CANVAS_HEIGHT / 2,
  }
}

const SLIDE_FOCUS_PADDING = 24

/** Breathing room (screen px) when the canvas frames ALL content on page load — larger than the per-slide
 *  focus padding so the deck doesn't hug the viewport edges on first paint. */
export const CANVAS_INITIAL_FIT_PADDING = 96

export function getSlideFitViewAtPosition(
  worldX: number,
  worldY: number,
  viewportWidth: number,
  viewportHeight: number,
  zoomMultiplier = 1,
  padding = SLIDE_FOCUS_PADDING,
  minZoom = 0.05,
  maxZoom = 4,
) {
  const fitZoom = Math.min(
    (viewportWidth - padding * 2) / CANVAS_WIDTH,
    (viewportHeight - padding * 2) / CANVAS_HEIGHT,
    maxZoom,
  )
  const zoom = Math.max(minZoom, Math.min(maxZoom, fitZoom * zoomMultiplier))
  const centerX = worldX + CANVAS_WIDTH / 2
  const centerY = worldY + CANVAS_HEIGHT / 2

  return {
    pan: {
      x: viewportWidth / 2 - centerX * zoom,
      y: viewportHeight / 2 - centerY * zoom,
    },
    zoom,
  }
}

/** Fit an arbitrary world-space AABB into the viewport (centred, with padding) — used to frame ALL
 *  canvas content (slides + doc cards + free elements) on load. Mirrors `getSlideFitViewAtPosition`'s
 *  math but for a general rect instead of one slide-sized box. */
export function getFitViewForWorldRect(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  viewportWidth: number,
  viewportHeight: number,
  padding = SLIDE_FOCUS_PADDING,
  minZoom = 0.05,
  maxZoom = 4,
) {
  const contentW = Math.max(1, bounds.maxX - bounds.minX)
  const contentH = Math.max(1, bounds.maxY - bounds.minY)
  const fitZoom = Math.min(
    (viewportWidth - padding * 2) / contentW,
    (viewportHeight - padding * 2) / contentH,
    maxZoom,
  )
  const zoom = Math.max(minZoom, Math.min(maxZoom, fitZoom))
  const centerX = (bounds.minX + bounds.maxX) / 2
  const centerY = (bounds.minY + bounds.maxY) / 2
  return {
    pan: {
      x: viewportWidth / 2 - centerX * zoom,
      y: viewportHeight / 2 - centerY * zoom,
    },
    zoom,
  }
}

export function getSlideFitView(
  slideIndex: number,
  viewportWidth: number,
  viewportHeight: number,
  zoomMultiplier = 1,
  padding = SLIDE_FOCUS_PADDING,
  minZoom = 0.05,
  maxZoom = 4,
) {
  const { x: worldX, y: worldY } = getSlideWorldPosition(slideIndex)
  return getSlideFitViewAtPosition(
    worldX,
    worldY,
    viewportWidth,
    viewportHeight,
    zoomMultiplier,
    padding,
    minZoom,
    maxZoom,
  )
}

// ─── Deck order: spatial scan (top→bottom, then left→right within a row) ────────────────────────────
// Slides are freely positioned, so the deck's order (Present mode + the sidebar rail) is no longer the
// `slides` array order — it's derived by reading the canvas like text: rows top-to-bottom, slides
// left-to-right within a row. Two slides count as the same row when their tops are within ~half a slide
// height of each other (so a row that isn't perfectly level still reads as one row).

const SLIDE_ROW_BAND = CANVAS_HEIGHT / 2

/** Order comparator for two slides by canvas position: row band first (top→bottom), then x (left→right),
 *  with id as a stable final tiebreaker so the sort is deterministic for exactly-coincident slides. */
function compareSlidesSpatial(
  a: Pick<Slide, 'id' | 'x' | 'y' | 'gridCol' | 'gridRow'>,
  b: Pick<Slide, 'id' | 'x' | 'y' | 'gridCol' | 'gridRow'>,
  aIndex: number,
  bIndex: number,
): number {
  const pa = getSlideWorldPositionForSlide(a, aIndex)
  const pb = getSlideWorldPositionForSlide(b, bIndex)
  if (Math.abs(pa.y - pb.y) > SLIDE_ROW_BAND) return pa.y - pb.y
  if (pa.x !== pb.x) return pa.x - pb.x
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** A copy of `slides` in deck (reading) order. Does NOT mutate the input — callers that need playback /
 *  sidebar order sort a copy at the point of use; the stored `slides` array order is left untouched. */
export function sortSlidesSpatially<T extends Pick<Slide, 'id' | 'x' | 'y' | 'gridCol' | 'gridRow'>>(
  slides: readonly T[],
): T[] {
  const indexById = new Map(slides.map((slide, index) => [slide.id, index]))
  return [...slides].sort((a, b) =>
    compareSlidesSpatial(a, b, indexById.get(a.id) ?? 0, indexById.get(b.id) ?? 0),
  )
}

// ─── Placement: next free grid-aligned spot for a new slide ─────────────────────────────────────────
// New/duplicated/pasted slides drop into the first open position when scanning the implicit grid in
// reading order (row 0 left→right, then row 1, …). "Open" = no existing slide overlaps that cell's rect.
// Slides can still be dragged to overlap afterwards; this is only the initial, tidy landing spot.

const SLIDE_PLACEMENT_MAX_COLS = 64 // generous scan width; a deck never has this many slides per row

/** World rects of all slides, using each slide's effective position (free x/y or grid fallback). */
function slideWorldRects(slides: readonly Slide[]) {
  return slides.map((slide, index) => {
    const { x, y } = getSlideWorldPositionForSlide(slide, index)
    return { x, y, width: CANVAS_WIDTH, height: CANVAS_HEIGHT }
  })
}

function rectsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
}

/**
 * First grid-aligned world position (top→bottom, left→right) whose slide-sized rect overlaps no existing
 * slide. Used to place a new slide tidily even though positioning is otherwise free.
 */
export function getNextFreeSlideWorldPosition(slides: readonly Slide[]): { x: number; y: number } {
  const rects = slideWorldRects(slides)
  let row = 0
  // Bounded outer loop: rows scanned is at most (#slides + 1), since each full row holds ≥1 slide.
  while (row <= slides.length) {
    for (let col = 0; col < SLIDE_PLACEMENT_MAX_COLS; col += 1) {
      const candidate = getSlideGridWorldPosition(col, row)
      const candidateRect = { ...candidate, width: CANVAS_WIDTH, height: CANVAS_HEIGHT }
      if (!rects.some((rect) => rectsOverlap(candidateRect, rect))) return candidate
    }
    row += 1
  }
  // Fallback (shouldn't hit): below everything.
  return getSlideGridWorldPosition(0, slides.length)
}

/** World position offset diagonally from a source slide — for duplicate/paste, mirroring doc cards. */
export function getOffsetSlideWorldPosition(
  source: Pick<Slide, 'x' | 'y' | 'gridCol' | 'gridRow'>,
  sourceIndex: number,
  offset = 64,
): { x: number; y: number } {
  const { x, y } = getSlideWorldPositionForSlide(source, sourceIndex)
  return { x: x + offset, y: y + offset }
}
